import { type FC, useState } from "react";
import type { ProfileLinkConfig } from "../../../../../client";
import { cn } from "../../../../../components/utils/cn";
import {
  DEFAULT_TEMPLATE_ID,
  availableTemplates,
} from "../../../../profile/templates/TemplateRenderer";
import AccordionSection from "./AccordionSection";

/**
 * TemplateLinkSettings — lets the user pick which CV template the link/PDF uses.
 *
 * NOTE (test wiring, step 6b variant b): `templateId` is not yet part of the
 * generated `ProfileLinkConfig` type, so we read/write it through a local cast.
 * To persist for real, add `templateId` to the API's ProfileLinkConfig model and
 * regenerate the client (`npm run openapi-ts`); the cast can then be removed.
 */

type WithTemplateId = { templateId?: string };

const getTemplateId = (linkConfig?: ProfileLinkConfig): string =>
  (linkConfig as WithTemplateId | undefined)?.templateId ?? DEFAULT_TEMPLATE_ID;

interface Props {
  disabled?: boolean;
  linkConfig?: ProfileLinkConfig;
  onUpdate: (payload: Partial<ProfileLinkConfig>) => void;
}

const TemplateLinkSettings: FC<Props> = ({ disabled, linkConfig, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const selectedId = getTemplateId(linkConfig);
  const selected =
    availableTemplates.find((t) => t.id === selectedId) ?? availableTemplates[0];

  return (
    <AccordionSection
      title="Template"
      subtitle={selected?.name ?? "Classic"}
      expanded={expanded}
      onToggle={() => setExpanded(!expanded)}
    >
      <div className="flex flex-col gap-1.5">
        {availableTemplates.map((t) => (
          <button
            type="button"
            key={t.id}
            disabled={disabled}
            onClick={() =>
              onUpdate({ templateId: t.id } as Partial<ProfileLinkConfig>)
            }
            className={cn(
              "rounded-lg border px-3 py-2 text-left text-sm transition-colors",
              selectedId === t.id
                ? "border-op-blue bg-op-blue text-white"
                : "border-gray-3 bg-white text-gray-6 hover:bg-gray-1",
              disabled ? "cursor-not-allowed opacity-60" : "",
            )}
          >
            {t.name}
          </button>
        ))}
      </div>
    </AccordionSection>
  );
};

export default TemplateLinkSettings;
